/**
 * Puppets (05 §6): a creature is a handful of rigid parts under one root, each a mesh built
 * with `MeshBuilder`, all sharing one material. Animation is setting the parts' transforms,
 * which is all a 16-pixel-tall creature needs; a skinned rig would buy nothing here.
 */

import { Group, type Material, Mesh, type Object3D, Vector3 } from 'three';
import type { MeshBuilder } from '../kit/MeshBuilder';

export type PartName = string;

/** How long a figure takes to turn round (a sprite flips at once; a model turning reads better). */
const TURN_SECONDS = 0.1;

export class Puppet {
  /** Where the creature stands: feet at the origin, facing +x before `facing` is applied. */
  readonly root = new Group();
  /** Turns the whole creature to face left or right (and a little toward the camera). */
  readonly facing = new Group();
  readonly parts = new Map<PartName, Object3D>();

  constructor(private readonly material: Material) {
    this.root.add(this.facing);
  }

  /** Add a part built by `mb`, pivoting at (px, py, pz) in the creature's frame, under `parent`. */
  add(name: PartName, mb: MeshBuilder, px = 0, py = 0, pz = 0, parent?: PartName): Mesh {
    const mesh = new Mesh(mb.build(), this.material);
    mesh.position.set(px, py, pz);
    // Where the part sits at rest: poses offset from this, so reshaping a model never breaks a pose.
    mesh.userData['rest'] = new Vector3(px, py, pz);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    const host = parent === undefined ? this.facing : this.parts.get(parent);
    if (!host) throw new Error(`puppet: no parent part "${parent ?? ''}"`);
    host.add(mesh);
    this.parts.set(name, mesh);
    return mesh;
  }

  /** A part by name; throws on a typo so a pose can never silently do nothing. */
  part(name: PartName): Object3D {
    const p = this.parts.get(name);
    if (!p) throw new Error(`puppet: no part "${name}"`);
    return p;
  }

  /**
   * How far the figure turns toward the camera from its facing, radians. Profile (0) reads
   * worst at this size; the dragons turn well round so their faces show, as on the box art.
   */
  turn = 0.5;

  /**
   * Face left (-1) or right (1), turned `turn` toward the camera. Facing left is the mirror
   * image, not a half-turn, so the side of the figure toward the camera - its near eye and
   * cheek, where its tail sweeps - is the same both ways, as a sprite's mirrored frames are.
   */
  face(dir: 1 | -1): void {
    this.heading = dir;
    this.applyHeading();
  }

  /** Where the figure is between facing left (-1) and right (1); 0 is square to the camera. */
  private heading = 1;
  private lastT = -1;

  /**
   * Turn toward `dir` at time `t` (seconds), taking TURN_SECONDS: round through facing the camera,
   * where the mirror flips over unseen, instead of snapping to the mirror image. The first call
   * faces at once.
   */
  turnTo(dir: 1 | -1, t: number): void {
    const dt = this.lastT < 0 ? TURN_SECONDS : Math.min(TURN_SECONDS, Math.max(0, t - this.lastT));
    this.lastT = t;
    const d = (2 * dt) / TURN_SECONDS;
    this.heading = dir > this.heading ? Math.min(dir, this.heading + d) : Math.max(dir, this.heading - d);
    this.applyHeading();
  }

  private applyHeading(): void {
    const side = this.heading >= 0 ? 1 : -1;
    const away = this.turn + (1 - Math.abs(this.heading)) * (Math.PI / 2 - this.turn);
    this.facing.rotation.y = -away * side;
    this.facing.scale.x = side;
  }

  /** The rest position of a part (see `add`). */
  rest(name: PartName): Vector3 {
    const r: unknown = this.part(name).userData['rest'];
    return r instanceof Vector3 ? r : new Vector3();
  }

  /** Put a part back at rest, offset by (dx, dy, dz), with no rotation. */
  pose(name: PartName, dx = 0, dy = 0, dz = 0): Object3D {
    const part = this.part(name);
    const r = this.rest(name);
    part.position.set(r.x + dx, r.y + dy, r.z + dz);
    part.rotation.set(0, 0, 0);
    return part;
  }

  dispose(): void {
    for (const p of this.parts.values()) if (p instanceof Mesh) p.geometry.dispose();
  }
}
